import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { db } from "../../firebase";

const ROLE_OPTIONS = [
  { key: "all", label: "Усі ролі" },
  { key: "librarian", label: "Бібліотекар" },
  { key: "maister", label: "Майстер" },
  { key: "business", label: "Бізнес-партнер" },
];

const STATUS_OPTIONS = [
  { key: "all", label: "Усі статуси" },
  { key: "pending", label: "Нові" },
  { key: "in_review", label: "В роботі" },
  { key: "approved", label: "Схвалені" },
  { key: "rejected", label: "Відхилені" },
];

const STATUS_LABELS = {
  pending: "Нова",
  in_review: "В роботі",
  approved: "Схвалено",
  rejected: "Відхилено",
};

const styles = {
  wrap: { padding: 24, width: "100%" },
  title: { margin: "0 0 18px", fontSize: 28 },
  card: {
    background: "#fff",
    border: "1px solid #e8e8e8",
    borderRadius: 14,
    padding: 18,
    marginBottom: 18,
    boxShadow: "0 8px 24px rgba(0,0,0,.04)",
  },
  filters: { display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 },
  input: {
    border: "1px solid #d7d7d7",
    borderRadius: 9,
    padding: "10px 12px",
    fontSize: 15,
  },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 900 },
  th: {
    textAlign: "left",
    padding: 10,
    borderBottom: "1px solid #ddd",
    background: "#f8f8f8",
  },
  td: { padding: 10, borderBottom: "1px solid #eee", verticalAlign: "top" },
  button: {
    border: "none",
    background: "#3e51c1",
    color: "#fff",
    borderRadius: 9,
    padding: "9px 12px",
    cursor: "pointer",
    fontWeight: 700,
  },
  buttonLight: {
    border: "1px solid #ccc",
    background: "#fff",
    color: "#222",
    borderRadius: 9,
    padding: "9px 12px",
    cursor: "pointer",
  },
  approve: {
    border: "none",
    background: "#16833a",
    color: "#fff",
    borderRadius: 9,
    padding: "9px 12px",
    cursor: "pointer",
    fontWeight: 700,
  },
  reject: {
    border: "none",
    background: "#c62828",
    color: "#fff",
    borderRadius: 9,
    padding: "9px 12px",
    cursor: "pointer",
    fontWeight: 700,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.55)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  modal: {
    width: "min(980px, 100%)",
    maxHeight: "92vh",
    overflow: "auto",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 20px 60px rgba(0,0,0,.24)",
  },
  modalHead: {
    padding: "20px 22px",
    borderBottom: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
  },
  modalBody: { padding: 22 },
  close: {
    border: "none",
    background: "#f1f1f1",
    borderRadius: 999,
    width: 36,
    height: 36,
    cursor: "pointer",
    fontSize: 20,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 14,
  },
  answer: {
    border: "1px solid #eee",
    borderRadius: 10,
    padding: 12,
    marginBottom: 10,
  },
  textarea: {
    width: "100%",
    border: "1px solid #d7d7d7",
    borderRadius: 9,
    padding: "10px 12px",
    fontSize: 15,
    minHeight: 90,
    boxSizing: "border-box",
  },
  actions: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },
};

const formatDate = (value) => {
  if (!value) return "—";
  const date =
    typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("uk-UA");
};

const valueToText = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") {
    if (value.url) return value.name || value.url;
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

function AnswerValue({ value }) {
  if (value && typeof value === "object" && value.url) {
    return (
      <a href={value.url} target="_blank" rel="noreferrer noopener">
        {value.name || "Відкрити файл"}
      </a>
    );
  }

  if (Array.isArray(value)) {
    return <span>{value.length ? value.join(", ") : "—"}</span>;
  }

  return <span style={{ whiteSpace: "pre-wrap" }}>{valueToText(value)}</span>;
}

async function updateUserCategory(userId, nextCategory) {
  if (!userId) throw new Error("userId відсутній у заявці");

  try {
    await updateDoc(doc(db, "users", userId), {
      category: nextCategory,
      updatedAt: serverTimestamp(),
    });
    return;
  } catch (error) {
    console.warn(
      "Не вдалося оновити users/{uid}, пробуємо пошук по uid",
      error,
    );
  }

  const usersQuery = query(collection(db, "users"), where("uid", "==", userId));
  const snap = await getDocs(usersQuery);

  if (snap.empty) {
    throw new Error("Користувача не знайдено в users");
  }

  await Promise.all(
    snap.docs.map((userDoc) =>
      updateDoc(userDoc.ref, {
        category: nextCategory,
        updatedAt: serverTimestamp(),
      }),
    ),
  );
}

export default function RoleChangeRequestsAdmin() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [adminComment, setAdminComment] = useState("");
  const [processing, setProcessing] = useState(false);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, "roleChangeRequests"),
          orderBy("createdAt", "desc"),
        ),
      );
      setRequests(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (error) {
      console.error("Помилка завантаження заявок:", error);
      Swal.fire("Помилка", "Не вдалося завантажити заявки.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const filteredRequests = useMemo(() => {
    return requests.filter((request) => {
      const roleOk =
        roleFilter === "all" || request.requestedRoleKey === roleFilter;
      const statusOk =
        statusFilter === "all" ||
        (request.status || "pending") === statusFilter;
      return roleOk && statusOk;
    });
  }, [requests, roleFilter, statusFilter]);

  const openRequest = (request) => {
    setSelected(request);
    setAdminComment(request.adminComment || "");
  };

  const updateRequestStatus = async (request, nextStatus) => {
    setProcessing(true);
    try {
      const updateData = {
        status: nextStatus,
        adminComment: adminComment.trim(),
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (nextStatus === "rejected") {
        updateData.rejectionAcknowledged = false;
        updateData.rejectionAcknowledgedAt = null;
      }

      await updateDoc(doc(db, "roleChangeRequests", request.id), updateData);
      await loadRequests();
      setSelected((prev) =>
        prev ? { ...prev, status: nextStatus, adminComment } : prev,
      );
      Swal.fire("Готово", "Статус заявки оновлено.", "success");
    } catch (error) {
      console.error("Помилка оновлення статусу заявки:", error);
      Swal.fire("Помилка", "Не вдалося оновити заявку.", "error");
    } finally {
      setProcessing(false);
    }
  };
  const saveAdminComment = async () => {
    if (!selected || processing) return;

    setProcessing(true);

    try {
      const preparedComment = adminComment.trim();

      await updateDoc(doc(db, "roleChangeRequests", selected.id), {
        adminComment: preparedComment,
        updatedAt: serverTimestamp(),
      });

      await loadRequests();

      setSelected((previousRequest) =>
        previousRequest
          ? {
              ...previousRequest,
              adminComment: preparedComment,
            }
          : previousRequest,
      );

      Swal.fire(
        "Збережено",
        "Коментар буде відображено користувачу.",
        "success",
      );
    } catch (error) {
      console.error("Помилка збереження коментаря:", error);

      Swal.fire("Помилка", "Не вдалося зберегти коментар.", "error");
    } finally {
      setProcessing(false);
    }
  };
  const approveRequest = async () => {
    if (!selected) return;

    const confirmed = window.confirm(
      `Схвалити заявку та змінити роль користувача на «${selected.requestedRoleLabel}»?`,
    );
    if (!confirmed) return;

    setProcessing(true);
    try {
      await updateUserCategory(selected.userId, selected.requestedRoleLabel);
      await updateDoc(doc(db, "roleChangeRequests", selected.id), {
        status: "approved",
        adminComment,
        reviewedAt: serverTimestamp(),
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await loadRequests();
      setSelected((prev) =>
        prev ? { ...prev, status: "approved", adminComment } : prev,
      );
      Swal.fire("Схвалено", "Роль користувача оновлено.", "success");
    } catch (error) {
      console.error("Помилка схвалення заявки:", error);
      Swal.fire(
        "Помилка",
        error.message || "Не вдалося схвалити заявку.",
        "error",
      );
    } finally {
      setProcessing(false);
    }
  };

  const rejectRequest = async () => {
    if (!selected) return;

    const confirmed = window.confirm("Відхилити цю заявку?");
    if (!confirmed) return;

    await updateRequestStatus(selected, "rejected");
  };

  return (
    <div style={styles.wrap}>
      <h2 style={styles.title}>Запити на зміну ролі</h2>

      <div style={styles.card}>
        <div style={styles.filters}>
          <select
            style={styles.input}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status.key} value={status.key}>
                {status.label}
              </option>
            ))}
          </select>

          <select
            style={styles.input}
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
          >
            {ROLE_OPTIONS.map((role) => (
              <option key={role.key} value={role.key}>
                {role.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            style={styles.buttonLight}
            onClick={loadRequests}
            disabled={loading}
          >
            Оновити
          </button>
        </div>

        {loading ? <p>Завантаження...</p> : null}

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Дата</th>
                <th style={styles.th}>Користувач</th>
                <th style={styles.th}>Контакти</th>
                <th style={styles.th}>Поточна роль</th>
                <th style={styles.th}>Бажана роль</th>
                <th style={styles.th}>Статус</th>
                <th style={styles.th}>Дія</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((request) => (
                <tr key={request.id}>
                  <td style={styles.td}>{formatDate(request.createdAt)}</td>
                  <td style={styles.td}>
                    {request.userName || request.userId || "—"}
                  </td>
                  <td style={styles.td}>
                    <div>
                      {request.contacts?.emailFromForm ||
                        request.userEmail ||
                        "—"}
                    </div>
                    <div>
                      {request.contacts?.phoneFromForm ||
                        request.userPhone ||
                        "—"}
                    </div>
                  </td>
                  <td style={styles.td}>{request.currentRole || "—"}</td>
                  <td style={styles.td}>{request.requestedRoleLabel || "—"}</td>
                  <td style={styles.td}>
                    {STATUS_LABELS[request.status || "pending"] ||
                      request.status}
                  </td>
                  <td style={styles.td}>
                    <button
                      type="button"
                      style={styles.button}
                      onClick={() => openRequest(request)}
                    >
                      Переглянути
                    </button>
                  </td>
                </tr>
              ))}
              {!filteredRequests.length && !loading ? (
                <tr>
                  <td style={styles.td} colSpan="7">
                    Заявок за вибраними фільтрами немає.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {selected ? (
        <div style={styles.overlay} onClick={() => setSelected(null)}>
          <div
            style={styles.modal}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={styles.modalHead}>
              <div>
                <h3 style={{ margin: 0 }}>{selected.formTitle || "Заявка"}</h3>
                <p style={{ margin: "6px 0 0" }}>
                  {selected.requestedRoleLabel} ·{" "}
                  {STATUS_LABELS[selected.status || "pending"] ||
                    selected.status}
                </p>
              </div>
              <button
                type="button"
                style={styles.close}
                onClick={() => setSelected(null)}
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ ...styles.grid, marginBottom: 18 }}>
                <div style={styles.card}>
                  <h4>Контакти з акаунта</h4>
                  <p>
                    Email:{" "}
                    {selected.contacts?.emailFromAccount ||
                      selected.userEmail ||
                      "—"}
                  </p>
                  <p>
                    Телефон:{" "}
                    {selected.contacts?.phoneFromAccount ||
                      selected.userPhone ||
                      "—"}
                  </p>
                  <p>Імʼя: {selected.userName || "—"}</p>
                  <p>User ID: {selected.userId || "—"}</p>
                </div>

                <div style={styles.card}>
                  <h4>Контакти з форми</h4>
                  <p>Email: {selected.contacts?.emailFromForm || "—"}</p>
                  <p>Телефон: {selected.contacts?.phoneFromForm || "—"}</p>
                  <p>Поточна роль: {selected.currentRole || "—"}</p>
                  <p>Бажана роль: {selected.requestedRoleLabel || "—"}</p>
                </div>
              </div>

              <h4>Відповіді форми</h4>
              {(selected.answers || []).map((answer) => (
                <div key={answer.fieldId} style={styles.answer}>
                  <strong>{answer.label}</strong>
                  <div style={{ marginTop: 6 }}>
                    <AnswerValue value={answer.value} />
                  </div>
                </div>
              ))}

              <div style={{ marginTop: 18 }}>
                <label
                  style={{ display: "block", fontWeight: 700, marginBottom: 8 }}
                >
                  Коментар адміністратора
                </label>
                <textarea
                  style={styles.textarea}
                  value={adminComment}
                  onChange={(event) => setAdminComment(event.target.value)}
                />
              </div>

              <div style={{ ...styles.actions, marginTop: 18 }}>
                <button
                  type="button"
                  style={styles.button}
                  onClick={saveAdminComment}
                  disabled={processing}
                >
                  Зберегти коментар
                </button>
                <button
                  type="button"
                  style={styles.buttonLight}
                  onClick={() => updateRequestStatus(selected, "in_review")}
                  disabled={processing}
                >
                  Взяти в роботу
                </button>
                <button
                  type="button"
                  style={styles.approve}
                  onClick={approveRequest}
                  disabled={processing}
                >
                  Схвалити і змінити роль
                </button>
                <button
                  type="button"
                  style={styles.reject}
                  onClick={rejectRequest}
                  disabled={processing}
                >
                  Відхилити
                </button>
                <button
                  type="button"
                  style={styles.buttonLight}
                  onClick={() => setSelected(null)}
                  disabled={processing}
                >
                  Закрити
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
